import { Component, computed, effect, input, signal } from '@angular/core';

import {
  BaseFormControl,
  BaseSignalInput,
  SignalCheckbox,
  SignalMultiSelectDropdown,
} from '@vallift/ngl';
import { constants } from '@core/constants/constants';

export type MultiChoiceGridType = 'button' | 'checkbox' | 'dropdown';

export interface MultiChoiceGridChoice {
  value: string;
  label: string;
}

export interface MultiChoiceGridItem {
  id: string;
  uniqueKey: string;
  display: string;
}

export interface MultiChoiceGridOptions {
  id?: string;
  uniqueKey?: string;
  display: string;
  selectedChoiceIds: string[];
}

/** Prefer selectedChoiceIds; fall back to legacy selectedChoices. */
function readSelectedChoiceIds(
  row: { selectedChoiceIds?: string[]; selectedChoices?: string[] } | undefined,
): string[] {
  if (!row) {
    return [];
  }
  if (Array.isArray(row.selectedChoiceIds)) {
    return row.selectedChoiceIds.filter(
      (id): id is string => typeof id === 'string' && id.trim().length > 0,
    );
  }
  if (Array.isArray(row.selectedChoices)) {
    return row.selectedChoices.filter(
      (id): id is string => typeof id === 'string' && id.trim().length > 0,
    );
  }
  return [];
}

@Component({
  selector: 'app-multi-choice-grid',
  standalone: true,
  imports: [BaseSignalInput, SignalMultiSelectDropdown, SignalCheckbox],
  templateUrl: './multi-choice-grid.html',
})
export class MultiChoiceGridConfig extends BaseFormControl<MultiChoiceGridOptions[]> {
  readonly label = input('');
  readonly helpText = input('');

  readonly items = input.required<(string | MultiChoiceGridItem)[]>();

  readonly options = input.required<MultiChoiceGridChoice[]>();

  readonly responseType = input<MultiChoiceGridType>('checkbox');

  readonly allowAdditionalRows = input(false);
  readonly addRowButtonLabel = input('+ Add another item');

  readonly previewMode = input<'desktop' | 'mobile'>('desktop');

  readonly locallyTouched = signal(false);

  readonly rows = computed<MultiChoiceGridOptions[]>(() => {
    const value = this.value();
    const current = Array.isArray(value) ? value : [];
    return this.syncRowsWithItems(this.items(), current);
  });

  readonly seedItemCount = computed(() => this.items().length);

  readonly effectiveType = computed<MultiChoiceGridType>(() => {
    const optionsCount = this.options().length;

    if (optionsCount > constants.multiChoiceGridMaxButtonOptions) {
      return 'dropdown';
    }

    return this.responseType();
  });

  readonly localError = computed<string | null>(() => {
    if (!this.required()) {
      return null;
    }

    const rows = this.rows();
    const seedCount = this.seedItemCount();

    if (rows.length === 0) {
      return 'Please complete all items.';
    }

    const incomplete = rows.some((row, index) => {
      const hasSelection = readSelectedChoiceIds(row).length > 0;

      const hasDisplay = row.display.trim().length > 0;

      if (index >= seedCount && !hasDisplay) {
        return true;
      }

      return !hasSelection;
    });

    return incomplete ? 'Please complete all items.' : null;
  });

  readonly displayMessage = computed<string | null>(() => {
    if (this.disabled()) {
      return null;
    }

    if (this.showError()) {
      return this.errorMessage() ?? this.localError();
    }

    if (this.locallyTouched() || this.touched()) {
      return this.localError();
    }

    return null;
  });

  readonly displayError = computed(() => this.displayMessage() !== null);

  constructor() {
    super();

    effect(() => {
      const items = this.items();
      const currentValue = this.value();

      const current = Array.isArray(currentValue) ? currentValue : [];

      const synced = this.syncRowsWithItems(items, current);

      if (!this.areRowsEqual(synced, current)) {
        this.value.set(synced);
      }
    });
  }

  isSelected(rowIndex: number, optionValue: string): boolean {
    return readSelectedChoiceIds(this.rows()[rowIndex]).includes(optionValue);
  }

  /**
   * @param checked When provided (checkbox valueChange), set selection explicitly.
   *   When omitted (button click), toggle.
   */
  selectOption(rowIndex: number, optionValue: string, checked?: boolean): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const row = this.rows()[rowIndex];

    if (!row) {
      return;
    }

    const selected = readSelectedChoiceIds(row);
    const shouldSelect =
      checked === undefined ? !selected.includes(optionValue) : checked;

    const selectedChoiceIds = shouldSelect
      ? selected.includes(optionValue)
        ? selected
        : [...selected, optionValue]
      : selected.filter((value) => value !== optionValue);

    this.updateRow(rowIndex, { selectedChoiceIds });
    this.markLocallyTouched();
  }

  updateChoices(rowIndex: number, values: unknown): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const selectedChoiceIds = Array.isArray(values)
      ? values.filter((value): value is string => typeof value === 'string')
      : [];

    this.updateRow(rowIndex, { selectedChoiceIds });
    this.markLocallyTouched();
  }

  updateDisplay(rowIndex: number, display: string): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    this.updateRow(rowIndex, { display });
  }

  addRow(): void {
    if (this.disabled() || this.readonly() || !this.allowAdditionalRows()) {
      return;
    }

    this.value.set([
      ...this.rows(),
      {
        display: '',
        selectedChoiceIds: [],
      },
    ]);

    this.markLocallyTouched();
  }

  removeRow(rowIndex: number): void {
    if (this.disabled() || this.readonly() || !this.canRemoveRow(rowIndex)) {
      return;
    }

    this.value.set(this.rows().filter((_, index) => index !== rowIndex));

    this.markLocallyTouched();
  }

  canRemoveRow(rowIndex: number): boolean {
    return rowIndex >= this.seedItemCount();
  }

  isUserAddedRowIndex(rowIndex: number): boolean {
    return rowIndex >= this.seedItemCount();
  }

  markLocallyTouched(): void {
    this.locallyTouched.set(true);
    this.onBlur();
  }

  private updateRow(rowIndex: number, updates: Partial<MultiChoiceGridOptions>): void {
    this.value.set(
      this.rows().map((row, index) =>
        index === rowIndex ? { ...row, ...updates } : row,
      ),
    );
  }

  private syncRowsWithItems(
    items: (string | MultiChoiceGridItem)[],
    current: MultiChoiceGridOptions[],
  ): MultiChoiceGridOptions[] {
    const seeded = items.map((item, index) => {
      const isCollectionItem = typeof item !== 'string';

      const display = isCollectionItem ? item.display : item;
      const itemId = isCollectionItem ? item.id : undefined;
      const itemUniqueKey = isCollectionItem
        ? item.uniqueKey || item.id
        : undefined;

      const existing =
        current.find((row) => itemId != null && row.id === itemId) ??
        current.find(
          (row) =>
            itemUniqueKey != null && row.uniqueKey === itemUniqueKey,
        ) ??
        (current[index]?.display === display
          ? current[index]
          : undefined) ??
        current.find((row) => row.display === display);

      return {
        ...(itemId ?? existing?.id
          ? { id: itemId ?? existing?.id }
          : {}),
        ...(itemUniqueKey ?? existing?.uniqueKey
          ? { uniqueKey: itemUniqueKey ?? existing?.uniqueKey }
          : {}),
        display,
        selectedChoiceIds: [...readSelectedChoiceIds(existing)],
      } satisfies MultiChoiceGridOptions;
    });

    return [...seeded, ...current.slice(items.length)];
  }

  private areRowsEqual(first: MultiChoiceGridOptions[], second: MultiChoiceGridOptions[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    return first.every((row, index) => {
      const other = second[index];

      if (!other) {
        return false;
      }

      const firstChoices = readSelectedChoiceIds(row);
      const secondChoices = readSelectedChoiceIds(other);

      return (
        row.id === other.id &&
        row.uniqueKey === other.uniqueKey &&
        row.display === other.display &&
        firstChoices.length === secondChoices.length &&
        firstChoices.every((value, choiceIndex) => value === secondChoices[choiceIndex])
      );
    });
  }
}
